import { call, put, select, take } from 'redux-saga/effects';
import * as api from 'reduxStore/apiService';
import LocalStorage from 'common/local-storage';
import { trackDiscountSaved } from 'components/menu/tracking/menuSegmentTracking';

import { GET_DISCOUNT_RULE_RECEIVE } from 'reduxStore/actionsConst';

import {
  requestDiscountRuleAction,
  receiveDiscountRuleAction,
  requestDiscountRuleFailureAction,
  receiveVenueDetailsAction,
  requestVenueDetailsFailureAction,
  updateDiscountRuleSuccessAction,
  updateDiscountRuleFailureAction,
  createDiscountRuleSuccessAction,
  createDiscountRuleFailureAction,
  removeDiscountRuleSuccessAction,
  removeDiscountRuleFailureAction,
} from './actions';

import { displayEmailCampaignDialog } from './EmailCampaignDialog/actions';

// @ts-expect-error ts-migrate(2691) FIXME: An import path cannot end with a '.tsx' extension.... Remove this comment to see the full error message
import { LS_KEY as DISABLE_EMAIL_CAMPAIGN_LS_KEY } from './EmailCampaignDialog/index.tsx';

/**
 * The rule as it was before this save. The reducer leaves `discountRules`
 * untouched until the refetch lands, so an edit can be diffed against it and a
 * delete can still report the name it removed (THY-2397). Ids are compared
 * numerically — the store holds numbers, but a caller passing the id from a URL
 * fragment would otherwise silently match nothing.
 */
export const selectStoredDiscountRule = (discountId) => (state) =>
  ((state.menuDiscount && state.menuDiscount.discountRules) || []).find(
    (rule) => rule && Number(rule.id) === Number(discountId),
  );

export function* getDiscountRuleDataRequested({ venueId }) {
  try {
    const data = yield call(api.get, `/api/venue/${venueId}/discount-rules.json`);

    yield put(receiveDiscountRuleAction(data));
  } catch (err) {
    yield put(requestDiscountRuleFailureAction(err));
  }
}

export function* getVenueDetailsDataRequested({ venueId }) {
  try {
    const data = yield call(api.get, `/api/venue/${venueId}.json?include=opening-hours`);

    yield put(receiveVenueDetailsAction(data));
  } catch (err) {
    yield put(requestVenueDetailsFailureAction(err));
  }
}

export function* putDiscountRuleDataRequested({ venueId, discountId, payload }) {
  try {
    const storedRule = yield select(selectStoredDiscountRule(discountId));
    const data = yield call(
      api.put,
      `/api/venue/${venueId}/discount-rule/${discountId}.json`,
      payload,
    );

    yield put(updateDiscountRuleSuccessAction(data, { venueId, discountId, payload }));

    trackDiscountSaved({ operation: 'edit', discountId, rule: payload, storedRule });

    yield put(requestDiscountRuleAction(venueId));

    if (!LocalStorage.getForVenue(DISABLE_EMAIL_CAMPAIGN_LS_KEY)) {
      // Wait for discount rules to be received
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      yield take(receiveDiscountRuleAction().type);

      yield put(displayEmailCampaignDialog(discountId));
    }
  } catch (ex) {
    yield put(updateDiscountRuleFailureAction(ex, { venueId, discountId, payload }));
  }
}

export function* addDiscountRuleDataRequested({ venueId, payload }) {
  try {
    const data = yield call(api.post, `/api/venue/${venueId}/discount-rule.json`, payload);

    yield put(createDiscountRuleSuccessAction(data, { venueId, payload }));

    // `data.id` is the server-assigned rule id; the request payload has none.
    trackDiscountSaved({ operation: 'add_new', discountId: data && data.id, rule: payload });

    yield put(requestDiscountRuleAction(venueId));

    if (!LocalStorage.getForVenue(DISABLE_EMAIL_CAMPAIGN_LS_KEY)) {
      // Wait for discount rules to be received
      yield take(GET_DISCOUNT_RULE_RECEIVE);

      yield put(displayEmailCampaignDialog(data.id));
    }
  } catch (ex) {
    yield put(createDiscountRuleFailureAction(ex, { venueId, payload }));
  }
}

export function* removeDiscountRuleDataRequested({ venueId, discountId }) {
  try {
    const storedRule = yield select(selectStoredDiscountRule(discountId));
    const data = yield call(api.del, `/api/venue/${venueId}/discount-rule/${discountId}.json`);

    yield put(removeDiscountRuleSuccessAction(data, { venueId }));

    // Delete carries no rule body, so the rule is read from the store to report
    // the name and settings that were removed.
    trackDiscountSaved({ operation: 'delete', discountId, storedRule });

    yield put(requestDiscountRuleAction(venueId));
  } catch (ex) {
    yield put(removeDiscountRuleFailureAction(ex, { venueId }));
  }
}
